import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Album,
  SquarePen,
  UserRoundPen,
  ChevronRight,
  School,
  History,
  FileText,
} from 'lucide-react';
import ChapterWise from '../components/practice/ChapterWise';
import MockTests from '../components/practice/MockTests';
import QuestionBanks from '../components/practice/QuestionBanks';
import SchoolQuestionBanks from '../components/practice/SchoolQuestionBanks';
import HistoryView from '../components/practice/HistoryView';
import AlertBanner from '../components/AlertBanner';
import LeaderboardView from '../components/practice/LeaderboardView';
import AdBanner from '../components/AdBanner';
import { useShuddhoshikhaContext } from '../hooks/useShuddhoshikhaContext';
import ProfileCompareView from '../components/practice/ProfileCompareView';
import ExamView from './ExamView';

export type SubView = 'main' | 'chapterWise' | 'mockTests' | 'questionBanks' | 'schoolQuestionBanks' | 'history' | 'user' | 'exams';

const categories = [
  {
    id: 'chapterWise' as SubView,
    title: 'বিষয় ভিত্তিক প্রস্তুতি',
    subtitle: 'বিষয় ও অধ্যায় অনুযায়ী অনুশীলন',
    icon: Album,
    gradient: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    hoverBorder: 'hover:border-blue-200',
    hoverText: 'group-hover:text-blue-500',
    darkBg: 'dark:bg-sky-500/10',
    darkIcon: 'dark:text-sky-400',
    isDark: false,
  },
  {
    id: 'mockTests' as SubView,
    title: 'মক পরীক্ষা',
    subtitle: 'কাস্টমাইজড মক টেস্ট তৈরি করুন',
    icon: SquarePen,
    gradient: 'from-emerald-400 to-teal-500 shadow-teal-500/15',
    hoverBorder: 'hover:border-teal-200',
    hoverText: 'group-hover:text-teal-500',
    darkBg: 'dark:bg-emerald-500/10',
    darkIcon: 'dark:text-emerald-400',
    isDark: false,
  },
  {
    id: 'exams' as SubView,
    title: 'মডেল টেস্ট',
    subtitle: 'লাইভ মডেল টেস্ট ও বোর্ডভিত্তিক পরীক্ষা',
    icon: FileText,
    gradient: 'from-pink-400 to-rose-500 shadow-rose-500/15',
    hoverBorder: 'hover:border-rose-200',
    hoverText: 'group-hover:text-rose-500',
    darkBg: 'dark:bg-pink-500/10',
    darkIcon: 'dark:text-pink-400',
    isDark: false,
  },
  {
    id: 'questionBanks' as SubView,
    title: 'বোর্ড প্রশ্ন প্রস্তুতি',
    subtitle: 'বোর্ড পরীক্ষার প্রশ্নাবলী',
    icon: UserRoundPen,
    gradient: 'from-amber-400 to-orange-500 shadow-orange-500/15',
    hoverBorder: 'hover:border-orange-200',
    hoverText: 'group-hover:text-orange-500',
    darkBg: 'dark:bg-amber-500/10',
    darkIcon: 'dark:text-amber-400',
    isDark: false,
  },
  {
    id: 'schoolQuestionBanks' as SubView,
    title: 'স্কুল প্রশ্নব্যাংক',
    subtitle: 'শীর্ষস্থানীয় স্কুলগুলোর প্রশ্নাবলী',
    icon: School,
    gradient: 'from-violet-400 to-purple-500 shadow-purple-500/15',
    hoverBorder: 'hover:border-purple-200',
    hoverText: 'group-hover:text-purple-500',
    darkBg: 'dark:bg-purple-500/10',
    darkIcon: 'dark:text-purple-400',
    isDark: false,
  },
  {
    id: 'history' as SubView,
    title: 'রিভিশন',
    subtitle: 'দাগানো প্রশ্ন ও ভুল উত্তরের রেকর্ড',
    icon: History,
    gradient: 'from-slate-500 to-slate-700 shadow-slate-600/15',
    hoverBorder: 'hover:border-slate-300',
    hoverText: 'group-hover:text-slate-600 dark:group-hover:text-slate-400',
    darkBg: 'dark:bg-slate-500/10',
    darkIcon: 'dark:text-slate-400',
    isDark: false,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export interface PracticeViewProps {
  subView: SubView;
  onSubViewChange: (subView: SubView) => void;
  isDarkMode?: boolean;
  subscriptionTier?: 'free' | 'pro' | 'max';
  onViewChange?: (view: string) => void;
}

export default function PracticeView({ subView, onSubViewChange, isDarkMode, onViewChange }: PracticeViewProps) {
  const [showBanner, setShowBanner] = useState(true);
  
  // Get user ID from URL if subView is 'user'
  let targetUserId: number | null = null;
  if (subView === 'user') {
    const params = new URLSearchParams(window.location.search);
    const idVal = params.get('id');
    if (idVal) {
      targetUserId = parseInt(idVal, 10);
    }
  }
  const { availableQuestions } = useShuddhoshikhaContext();


  const toBengaliNumber = (num: number): string => {
    const englishToBengali: Record<string, string> = {
      '0': '০', '1': '১', '2': '২', '3': '৩', '4': '৪',
      '5': '৫', '6': '৬', '7': '৭', '8': '৮', '9': '৯'
    };
    return num
      .toLocaleString('en-US')
      .split('')
      .map(char => englishToBengali[char] || char)
      .join('');
  };

  const getDynamicSubtitle = (id: SubView, originalSubtitle: string) => {
    if (!availableQuestions) return originalSubtitle;
    if (id === 'main' || id === 'history') return originalSubtitle;
    const count = (availableQuestions as any)[id];
    if (count === undefined) return originalSubtitle;
    return `${originalSubtitle} • ${toBengaliNumber(count)}+ প্রশ্ন`;
  };

  const handleSubViewChange = (view: SubView) => {
    onSubViewChange(view);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <>
        {subView === 'main' && (
          <motion.div
            key="main"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-6"
          >
            <motion.div variants={itemVariants}>
              <h2 className="font-bangla text-2xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">অনুশীলন</h2>
              <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD] mt-0.5">আপনার প্রস্তুতির জন্য বিভিন্ন অনুশীলন টুল</p>
            </motion.div>

            {/* Alert Banner */}
            <AnimatePresence>
              {showBanner && (
                <motion.div
                  variants={itemVariants}
                  exit={{ opacity: 0, y: -15, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <AlertBanner onClose={() => setShowBanner(false)} />
                </motion.div>
              )}
            </AnimatePresence>


            {/* Categories */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">অনুশীলনের ধরণ</h3>
              
              {/* Desktop Layout (Grid of 6 items) */}
              <div className="hidden lg:grid lg:grid-cols-3 gap-4">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleSubViewChange(cat.id)}
                      className={`group p-5 rounded-2xl border transition-all duration-300 text-left cursor-pointer ${
                        cat.isDark
                          ? 'bg-[#1E293B] text-white border-slate-800 shadow-[0_4px_16px_rgba(0,0,0,0.1)] hover:bg-[#2e3b52] hover:border-slate-700 hover:-translate-y-0.5'
                          : `bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border border-gray-100 dark:border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.02)] dark:shadow-none hover:shadow-[0_12px_30px_rgba(0,0,0,0.06)] dark:hover:shadow-[#8AB4F8]/5 hover:-translate-y-0.5 ${cat.hoverBorder}`
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border border-white/10 relative overflow-hidden flex-shrink-0 transition-all duration-200 ${
                          isDarkMode 
                            ? `${cat.darkBg} ${cat.darkIcon}` 
                            : `bg-gradient-to-tr ${cat.gradient} text-white`
                        }`}>
                          {!isDarkMode && <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />}
                          <Icon className="w-5.5 h-5.5" />
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-colors ${
                          cat.isDark
                            ? 'text-slate-400 group-hover:text-white'
                            : 'text-gray-300 dark:text-[#A0A5AD] group-hover:text-gray-500 dark:group-hover:text-[#8AB4F8]'
                        }`} />
                      </div>
                      <h4 className={`font-bangla text-base font-bold transition-colors text-[#1F1F1F] practice-card-title-${cat.id} ${cat.hoverText}`}>
                        {cat.title}
                      </h4>
                      <p className={`font-bangla text-xs mt-1.5 leading-relaxed ${
                        cat.isDark ? 'text-slate-300' : 'text-gray-400 dark:text-[#A0A5AD]'
                      }`}>
                        {getDynamicSubtitle(cat.id, cat.subtitle)}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Mobile Layout (Grid of 3 items + Full width gradient banner at the bottom) */}
              <div className="grid lg:hidden gap-4">
                {/* 2-Column Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {categories
                    .filter((cat) => cat.id !== 'mockTests' && cat.id !== 'history')
                    .map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => handleSubViewChange(cat.id)}
                          className="flex flex-col items-center justify-center p-3 rounded-2xl bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md border border-gray-100 dark:border-white/10 shadow-[0_4px_12px_rgba(0,0,0,0.02)] active:scale-95 transition-all text-center cursor-pointer min-h-[110px]"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2.5 shadow-md relative overflow-hidden transition-all duration-200 ${
                            isDarkMode 
                              ? `${cat.darkBg} ${cat.darkIcon}` 
                              : `bg-gradient-to-tr ${cat.gradient} text-white`
                          }`}>
                            {!isDarkMode && <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />}
                            <Icon className="w-4.5 h-4.5" />
                          </div>
                          <h4 className={`font-bangla text-[11px] sm:text-xs font-bold text-[#1F1F1F] leading-snug practice-card-title-${cat.id}`}>
                            {cat.title}
                          </h4>
                        </button>
                      );
                    })}
                </div>

                {/* Banner Button (Mock Test) */}
                {(() => {
                  const mockCat = categories.find((cat) => cat.id === 'mockTests')!;
                  const Icon = mockCat.icon;
                  return (
                    <button
                      onClick={() => handleSubViewChange(mockCat.id)}
                      className="w-full rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#4F46E5] dark:from-[#3B1F8F] dark:to-[#1E1B4B] p-4 flex items-center justify-between text-white shadow-md active:scale-[0.98] transition-all relative overflow-hidden cursor-pointer"
                    >
                      <div className="absolute inset-0 bg-white/5 dark:bg-transparent pointer-events-none" />
                      {/* Left contents */}
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                          isDarkMode 
                            ? 'bg-emerald-500/10 text-emerald-400' 
                            : 'bg-white/20 backdrop-blur-md text-white'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex flex-col items-start text-left min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bangla text-base font-bold leading-tight practice-card-title-mockTests">মক পরীক্ষা</span>
                            <span className="font-english text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded bg-white/20 uppercase">BETA</span>
                          </div>
                          <p className="font-bangla text-[11px] text-white/80 mt-1 truncate">
                            {getDynamicSubtitle('mockTests', 'নিজের পছন্দমত পরীক্ষা তৈরি করুন')}
                          </p>
                        </div>
                      </div>

                      {/* Right indicator */}
                      <ChevronRight className="w-5 h-5 text-white/70" />
                    </button>
                  );
                })()}

                {/* Banner Button (History) */}
                {(() => {
                  const histCat = categories.find((cat) => cat.id === 'history')!;
                  const Icon = histCat.icon;
                  return (
                    <button
                      onClick={() => handleSubViewChange(histCat.id)}
                      className="w-full rounded-2xl bg-white dark:bg-[#1E293B]/80 p-4 flex items-center justify-between text-[#1F1F1F] dark:text-white shadow-sm border border-gray-100 dark:border-slate-800 transition-all relative overflow-hidden cursor-pointer hover:bg-gray-50/50 dark:hover:bg-[#1E293B]/60"
                    >
                      <div className="absolute inset-0 bg-white/5 dark:bg-transparent pointer-events-none" />
                      {/* Left contents */}
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                          isDarkMode 
                            ? 'bg-slate-500/10 text-slate-400' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex flex-col items-start text-left min-w-0">
                          <span className="font-bangla text-base font-bold leading-tight practice-card-title-history">রিভিশন</span>
                          <p className="font-bangla text-[11px] text-gray-400 dark:text-slate-400 mt-1 truncate">
                            দাগানো প্রশ্ন ও ভুল উত্তরের রেকর্ড
                          </p>
                        </div>
                      </div>

                      {/* Right indicator */}
                      <ChevronRight className="w-5 h-5 text-gray-450 dark:text-slate-550" />
                    </button>
                  );
                })()}
              </div>
            </motion.div>


            {/* Leaderboard Section */}
            <LeaderboardView 
              isDarkMode={isDarkMode} 
              onViewUserProfile={(userId: number) => {
                window.history.pushState({
                  view: 'চর্চাঘর',
                  subView: 'user',
                  selectedCourseId: null
                }, '', `/dashboard/chorcaghor/user/?id=${userId}`);
                handleSubViewChange('user');
              }}
            />
            <AdBanner placement="leaderboard_bottom" />
          </motion.div>
        )}

        {subView === 'chapterWise' && (
          <motion.div
            key="chapterWise"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ChapterWise onBack={() => handleSubViewChange('main')} isDarkMode={isDarkMode} onViewChange={onViewChange} />
          </motion.div>
        )}

        {subView === 'mockTests' && (
          <motion.div
            key="mockTests"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <MockTests onBack={() => handleSubViewChange('main')} isDarkMode={isDarkMode} />
          </motion.div>
        )}

        {subView === 'questionBanks' && (
          <motion.div
            key="questionBanks"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <QuestionBanks onBack={() => handleSubViewChange('main')} isDarkMode={isDarkMode} />
          </motion.div>
        )}

        {subView === 'schoolQuestionBanks' && (
          <motion.div
            key="schoolQuestionBanks"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <SchoolQuestionBanks onBack={() => handleSubViewChange('main')} isDarkMode={isDarkMode} />
          </motion.div>
        )}

        {subView === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <HistoryView onBack={() => handleSubViewChange('main')} isDarkMode={isDarkMode} />
          </motion.div>
        )}

        {subView === 'exams' && (
          <motion.div
            key="exams"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ExamView onBack={() => handleSubViewChange('main')} />
          </motion.div>
        )}

        {subView === 'user' && (
          <motion.div
            key="user"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-6 shadow-sm border border-gray-50 dark:border-white/5">
              <ProfileCompareView
                targetUserId={targetUserId || 0}
                onClose={() => {
                  window.history.pushState({
                    view: 'চর্চাঘর',
                    subView: 'main',
                    selectedCourseId: null
                  }, '', '/dashboard/chorcaghor/');
                  handleSubViewChange('main');
                }}
                isDarkMode={isDarkMode}
              />
            </div>
          </motion.div>
        )}
      </>
    </motion.div>
  );
}
